import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Section } from '../../../../../components/ui/Section';
import { CARBON_EMISSIONS_TREND, EsgActionItem, SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';
import EsgActionItemsTable from './EsgActionItemsTable';

export interface ExecutiveOverviewTabProps {
  profiles: SupplierEsgProfile[];
  actionItems: EsgActionItem[];
}

const ExecutiveOverviewTab: React.FC<ExecutiveOverviewTabProps> = ({ profiles, actionItems }) => {
  const { t } = useTranslation('esgDashboard');
  const highRiskSuppliers = profiles.filter((p) => p.riskLevel === 'High' || p.riskLevel === 'Critical');
  const attentionSuppliers = profiles.filter((p) => p.esgScore < 60 && p.riskLevel !== 'Critical' && p.riskLevel !== 'High');
  const expiringCerts = profiles.filter((p) => p.certificationsExpiringSoonCount > 0 || p.insuranceStatus === 'ExpiringSoon');

  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('overview.carbonEmissionsTrendTitle', 'Carbon emissions trend (total CO\u2082e, tonnes)')}</Typography>
            <ResponsiveContainer width="100%"
              height={220}>
              <LineChart data={CARBON_EMISSIONS_TREND}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone"
                  dataKey="co2e"
                  stroke="#2e7d32"
                  strokeWidth={2}
                  dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('overview.highRiskSuppliersTitle', 'High risk suppliers ({{count}})', { count: highRiskSuppliers.length })}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell>{t('common.category', 'Category')}</TableCell>
                  <TableCell align="right">{t('common.esgScore', 'ESG score')}</TableCell>
                  <TableCell align="right">{t('common.risk', 'Risk')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highRiskSuppliers.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell align="right">{p.esgScore}</TableCell>
                    <TableCell align="right">{p.riskLevel}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('overview.attentionSuppliersTitle', 'Suppliers requiring attention ({{count}})', { count: attentionSuppliers.length })}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell>{t('common.category', 'Category')}</TableCell>
                  <TableCell align="right">{t('common.esgScore', 'ESG score')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attentionSuppliers.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell align="right">{p.esgScore}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('overview.expiringCertsTitle', 'Certifications expiring soon ({{count}})', { count: expiringCerts.length })}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell align="right">{t('overview.certsExpiring', 'Certs expiring')}</TableCell>
                  <TableCell align="right">{t('common.insurance', 'Insurance')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiringCerts.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="right">{p.certificationsExpiringSoonCount}</TableCell>
                    <TableCell align="right">{p.insuranceStatus}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('tiles.outstandingActions', 'Outstanding actions')}
              items={actionItems.slice(0, 8)} />
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default ExecutiveOverviewTab;
